import { showSpinner, hideSpinner, logApiError, hideForwardButton } from './ui';
import Api, { createEvent } from 'utils/api';
import AssetLoader from 'utils/assetLoader';

import { showAward } from './award';

import { getViewportSize } from 'utils/get-viewport-size';

import {
  GET_ACTIVITY_DATA_SUCCESS,
  ASSETS_LOADED_SUCCESS,
  ASSETS_LOADED_ERROR,
  START_ACTIVITY,
  NEXT_ACTIVITY,
  RESET_ACTIVITY_STORE,
  ACTIVITY_FLOW_COMPLETE,
  SET_LEVEL_OVERRIDE,
  TOGGLE_VIDEO,
  TOGGLE_MODAL,
  REPEAT_ACTIVITY,
  SKIP_RECORDING_ACTIVITY,
  COUNTERS_FLIP_CARD,
  RESET_RESTARTING_FLAG
} from '../types/activity';

import { errorLoggerStart, errorLoggerStop, errorLoggerReset } from './errorLogger';
import { clearRecordings } from 'store/actions/recordings';
import { hideVisualCue } from 'store/actions/ui';
import { AuthStrategy } from '@lwtears/lwt-common-frontend/lib/@common/util/api-util';

const { get, put, post } = Api;

export const toggleVideo = isVideoModalOpen => ({ type: TOGGLE_VIDEO, isVideoModalOpen });

export const toggleModal = isModalOpen => ({ type: TOGGLE_MODAL, isModalOpen });

export const skipRecordingActivity = isRecordingActivitySkipped => ({
  type: SKIP_RECORDING_ACTIVITY,
  isRecordingActivitySkipped
});

export const countersFlipCard = isCountersFlipCard => ({
  type: COUNTERS_FLIP_CARD,
  isCountersFlipCard
});

export const activityDataSuccess = data => ({ type: GET_ACTIVITY_DATA_SUCCESS, payload: data });

export const assetsLoadSuccess = sounds => ({ type: ASSETS_LOADED_SUCCESS, payload: sounds });

export const assetsLoadError = error => ({ type: ASSETS_LOADED_ERROR, payload: error });

export const startActivity = () => ({ type: START_ACTIVITY });

export const nextActivity = () => ({ type: NEXT_ACTIVITY });

export const repeatActivity = () => ({ type: REPEAT_ACTIVITY });

export const resetRestartingFlag = () => ({ type: RESET_RESTARTING_FLAG });

export const resetStore = () => ({ type: RESET_ACTIVITY_STORE });

export const setFlowComplete = () => ({ type: ACTIVITY_FLOW_COMPLETE });

export const setLevelOverride = level => ({ type: SET_LEVEL_OVERRIDE, payload: level });

export const getActivitySequence = character => async (dispatch, getState) => {
  dispatch(showSpinner());

  const {
    ui: { module, micPermissions: microphonePermission }
  } = getState();

  const { data, status, error } = await get('/students/me/next-sequence', {
    module,
    params: { character, microphonePermission }
  });

  dispatch(hideSpinner());

  if (status < 400 && data && !error) {
    dispatch(activityDataSuccess(data));
  } else {
    dispatch(logApiError(error));
  }

  return status;
};

export const loadActivityAssets = () => async (dispatch, getState) => {
  const {
    activity: {
      data: { activities = [] }
    }
  } = getState();

  if (!activities.length) {
    dispatch(assetsLoadSuccess());
    return;
  }

  const loader = new AssetLoader();

  activities.forEach(({ images = [], audio = [] }) => {
    images.forEach(image => loader.queueImage(image));
    audio.forEach(sound => loader.queueAudio(sound));
  });

  try {
    const result = await loader.downloadAll();

    const mappedRounds = activities.map(activity => ({
      ...activity,
      audio: activity.audio.reduce(
        (map, sound) => ({
          ...map,
          [sound.src]: result.get(sound.src)
        }),
        {}
      ),
      stopAllSounds: () => activity.audio.map(sound => result.get(sound.src).stop())
    }));

    dispatch(assetsLoadSuccess(mappedRounds));
    dispatch(handleActivityStart());
  } catch (error) {
    dispatch(assetsLoadError(error));
  }
};

export const resetActivityStore = () => (dispatch, getState) => {
  const { activity: { activities = [] } = {} } = getState();
  activities.forEach(({ audio }) => Object.keys(audio).forEach(key => audio[key].unload()));
  dispatch(resetStore());
};

export const handleActivityStart = () => dispatch => {
  dispatch(hideVisualCue());
  dispatch(startActivity());
  dispatch(errorLoggerStart());
};

export const handleRepeatActivity = () => dispatch => {
  dispatch(errorLoggerReset());
  dispatch(hideForwardButton());
  dispatch(repeatActivity());
  dispatch(errorLoggerStart());
  dispatch(hideVisualCue());
  setTimeout(() => {
    dispatch(resetRestartingFlag());
  });
};

export const endRound = () => (dispatch, getState) => {
  const {
    activity: {
      data: { activities },
      currentRoundIndex
    },
    ui: { appMode }
  } = getState();

  if (currentRoundIndex === activities.length - 1 || appMode === 'tester') {
    dispatch(errorLoggerReset());
    dispatch(setFlowComplete());
    dispatch(hideForwardButton());
    if (appMode === 'curriculum') {
      dispatch(showAward());
    }
  } else {
    dispatch(errorLoggerReset());
    dispatch(hideForwardButton());
    dispatch(nextActivity());
    dispatch(errorLoggerStart());
  }
  dispatch(hideVisualCue());
};

export const postProgressData = () => async (dispatch, getState) => {
  const {
    errorLogger: { startTime, endTime, errorArray },
    activity: {
      data: { activities },
      currentRoundIndex,
      isRecordingActivitySkipped
    },
    student: {
      classroom: { id: classroomId }
    },
    ui: { module }
  } = getState();

  const isLastRound = currentRoundIndex === activities.length - 1;

  const currentActivity = activities[currentRoundIndex] || null;

  const { sequenceActivityId } = currentActivity || 0;

  const { data: { rounds, puzzlePieces } = {} } = currentActivity;

  const elapsedTime = Math.floor((endTime - startTime) / 1000);

  const options = {
    module,
    data: {
      classroomId,
      sequenceActivityId,
      clientInfo: {
        viewport: getViewportSize()
      },
      progressData: {
        complete: isLastRound,
        elapsedTime,
        numPieces: puzzlePieces ? puzzlePieces.length : undefined,
        numRounds: rounds ? rounds.length : undefined
      },
      ...(errorArray.length ? { errorData: errorArray } : {})
    }
  };

  const { status, data, error } = await post(`/students/me/progress`, options);

  if (status >= 400 || error) return { error, status };

  const recordingActivityTypes = ['VoiceRecorder', 'WordRecorder', 'SoundRecorder'];
  if (
    !isRecordingActivitySkipped &&
    recordingActivityTypes.includes(activities[currentRoundIndex].type)
  ) {
    const { status, error } = await dispatch(postVoiceRecordings(data.id));

    dispatch(clearRecordings());

    return { status, error };
  } else {
    return { status, error };
  }
};

export const sendRecorderSkippedEventData = () => (_dispatch, getState) => {
  const {
    ui: { module }
  } = getState();
  createEvent('voiceRecorderSkipped', { skippedAudioRecorder: true, module });
};

export const sendTutorialEventData = (autoplay, src, didComplete) => (_dispatch, getState) => {
  const {
    ui: { module }
  } = getState();

  createEvent(didComplete ? 'tutorialCompleted' : 'tutorialStart', {
    tutorial: { autoplay, src },
    module
  });
};

export const markTutorialPlayed = () => (_dispatch, getState) => {
  const {
    activity: { activities, currentRoundIndex },
    ui: { module }
  } = getState();

  const { sequenceActivityId } = activities[currentRoundIndex] || {};

  put(`/${module}/students/me/tutorial`, { data: { sequenceActivityId } });
};

export const postVoiceRecordings = progressId => (dispatch, getState) => {
  const {
    recordings: { files },
    ui: { module }
  } = getState();

  if (!files || files.length === 0) return false;

  const data = new FormData();
  data.append('file', files[1]);
  data.append('draft', files[0]);

  return post(`/students/me/progress/${progressId}/files`, { module, data });
};

export const sendEventData = () => (dispatch, getState) => {
  const {
    activity: { activities, currentRoundIndex },
    errorLogger: { startTime, endTime, errorArray: errorData },
    ui: { module }
  } = getState();

  const isLastRound = currentRoundIndex === activities.length - 1;
  const elapsedTime = Math.floor((endTime - startTime) / 1000);
  const progressData = { complete: isLastRound, elapsedTime };

  createEvent('activityCompleted', {
    module,
    progressData,
    ...(errorData ? { errorCount: errorData.length } : {})
  });
};

export const handleRoundEnd = () => async (dispatch, getState) => {
  dispatch(errorLoggerStop());

  const {
    activity: {
      data: { activities },
      currentRoundIndex
    },
    ui: { appMode }
  } = getState();

  if (appMode === 'tester') dispatch(endRound());

  if (appMode === 'curriculum') {
    const { sequenceActivityId = 0 } = activities[currentRoundIndex] || {};

    if (!sequenceActivityId) {
      console.error('No sequenceActivityId for some reason...'); // eslint-disable-line no-console
      return;
    }

    dispatch(showSpinner());

    const { status } = await dispatch(postProgressData());

    if (status < 400) {
      dispatch(sendEventData());
      dispatch(endRound());
    } else {
      // error handling ?
      dispatch(logApiError());
    }

    dispatch(hideSpinner());
  }
};

export const setActivity = params => async dispatch => {
  dispatch(showSpinner());
  const { module, ...restParams } = params;
  const computeModule = module || (params.characterType !== 'number' ? 'letters' : 'numbers');

  const { status, data, error } = await get(`/${computeModule}/activities`, AuthStrategy.SESSION, {
    params: restParams
  });

  if (status < 400) {
    // TODO: Handle empty data set more appropriately. Maybe similar to the 'setTesterActivity' action?
    if (data.length) dispatch(activityDataSuccess(data[0]));
  } else {
    dispatch(logApiError(error));
  }

  dispatch(hideSpinner());

  return status;
};

export const setTesterActivity = ({ activityId, template, level, receivedModule }) => async (
  dispatch,
  getState
) => {
  if (!activityId && !template) return;

  dispatch(showSpinner());

  const {
    ui: { module }
  } = getState();

  const params = {
    ...(activityId && { id: activityId }),
    ...(template && { type: template }),
    ...(level && { level })
  };

  if (undefined !== testerData[template]) {
    dispatch(activityDataSuccess(testerData[template]));
    dispatch(hideSpinner());
    return 200;
  }

  const { status, data, error } = await get('/tester/activities', AuthStrategy.SESSION, {
    module: receivedModule || module,
    params
  });

  if (status < 400) {
    dispatch(activityDataSuccess(data));
  } else {
    dispatch(logApiError(error));
  }

  dispatch(hideSpinner());

  return status;
};

//for testing purposes
export const mockData = {
  title: 'Test 1 star',
  stars: 1,
  rounds: [
    {
      activityType: 'WordPicture',
      activityData: {
        word: 'one',
        photo: 'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg'
      },
      images: ['https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg'],
      sounds: [],
      videos: [],
      instructions: {
        audio: 'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/hippo-mt.mp3',
        video: '',
        text: ''
      }
    },
    {
      activityType: 'SomethingElse',
      images: [
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/kangaroo.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lizard.jpg'
      ],
      sounds: [
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lizard-mt.mp3'
      ],
      videos: [],
      instructions: {
        audio: 'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/hippo-mt.mp3',
        video: '',
        text: ''
      }
    },
    {
      activityType: 'FlipCard',
      images: [
        'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/images/flip-card-01-flamingo.png',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/kangaroo.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lizard.jpg'
      ],
      sounds: [
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lizard-mt.mp3'
      ],
      instructions: {
        audio: 'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/hippo-mt.mp3',
        video: '',
        text: ''
      },
      activityData: {
        frontFace:
          'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/images/flip-card-01-flamingo.png',
        backFace:
          'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/videos/flip-card-01-flamingo.mp4',
        beginningAudio:
          'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
        afterFlipAudio:
          'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3',
        endingAudio:
          'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lizard-mt.mp3'
      }
    },
    {
      activityType: 'SomethingElse',
      images: [
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/kangaroo.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lizard.jpg'
      ],
      sounds: [
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lizard-mt.mp3'
      ],
      videos: [],
      instructions: {
        audio: 'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/hippo-mt.mp3',
        video: '',
        text: ''
      }
    },
    {
      activityType: 'Counters',
      images: [
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/kangaroo.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lizard.jpg'
      ],
      audio: [
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2ce0c7fcc41.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2ce4ae9f871.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2ced124ec31.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cee6652fa1.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf04518b41.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf14b8ccc1.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf28f34ba1.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf418e75b1.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf5b51fce1.mp3',
        'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf8066d811.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3'
      ],
      data: {
        counters: 5,
        counterColor: '#ff0000',
        cardFront:
          'https://gss-content-qa.s3.amazonaws.com/activities/counters/images/counters-01-front.png',
        cardBack:
          'https://gss-content-qa.s3.amazonaws.com/activities/counters/images/counters-blank.png',
        instructions: {
          afterFLip:
            'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
          beforeFlip:
            'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3',
          tooMany: '',
          keepGoing: ''
        },
        numbers: {
          1: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2ce0c7fcc41.mp3',
          2: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2ce4ae9f871.mp3',
          3: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2ced124ec31.mp3',
          4: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cee6652fa1.mp3',
          5: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf04518b41.mp3',
          6: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf14b8ccc1.mp3',
          7: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf28f34ba1.mp3',
          8: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf418e75b1.mp3',
          9: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf5b51fce1.mp3',
          10: 'https://s3-ap-northeast-1.amazonaws.com/poodll-audioprocessing-out/CP/30/localhostuser/recordmp3online.com/poodll/poodllfile5db2cf8066d811.mp3'
        }
      }
    },
    {
      activityType: 'TagBag',
      images: [
        'https://gss-content-qa.s3.amazonaws.com/activities/tag-bags/images/tag-bags-01-closed.png',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lamb.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/kangaroo.jpg',
        'https://dev-cms.hwtears.com/sites/default/files/pl/a1/img/lizard.jpg'
      ],
      sounds: [
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lamb-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/kangaroo-mt.mp3',
        'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/lizard-mt.mp3'
      ],
      instructions: {
        audio: 'https://dev-cms.hwtears.com/sites/default/files/voice/words-phrases/hippo-mt.mp3',
        video: '',
        text: ''
      },
      data: {
        instructions: {
          beforeOpen:
            'https://gss-content-qa.s3.amazonaws.com/activities/tag-bags/audio/touch-to-open-the-tag-bag.mp3'
        },
        text: 'one',
        number: 'https://gss-content-qa.s3.amazonaws.com/activities/audio/01.mp3',
        bagClosed:
          'https://gss-content-qa.s3.amazonaws.com/activities/tag-bags/images/tag-bags-red-velcro.png',
        bagOpen:
          'https://gss-content-qa.s3.amazonaws.com/activities/tag-bags/images/tag-bags-01-red.png',
        bagAnimation:
          'https://gss-content-qa.s3.amazonaws.com/activities/tag-bags/videos/tag-bags-01-red.mp4',
        openingSound:
          'https://gss-content-qa.s3.amazonaws.com/activities/tag-bags/audio/tag-bags-velcro.mp3'
      }
    }
  ]
};

const testerData = {
  xWallCard: {
    character: {
      id: 27,
      character: 'F',
      characterCase: 'uppercase',
      characterType: 'print',
      locale: ['en-US']
    },
    level: 1,
    activities: [
      {
        id: 228,
        type: 'WallCard',
        level: 1,
        audio: [
          'https://gss-content-qa.s3.amazonaws.com/activities/audio/f-says-f.mp3',
          'https://gss-content-qa.s3.amazonaws.com/activities/wall-card/audio/like-in-the-word-fish.mp3'
        ],
        images: [
          'https://gss-content-qa.s3.amazonaws.com/activities/wall-card/images/wall-card-f.png'
        ],
        data: {
          instructions: {
            beforeAnimation: [
              'https://gss-content-qa.s3.amazonaws.com/activities/audio/f-says-f.mp3',
              'https://gss-content-qa.s3.amazonaws.com/activities/wall-card/audio/like-in-the-word-fish.mp3'
            ]
          },
          image:
            'https://gss-content-qa.s3.amazonaws.com/activities/wall-card/images/wall-card-f.png',
          animation:
            'https://gss-content-qa.s3.amazonaws.com/activities/wall-card/videos/wall-card-fish.mp4'
        }
      }
    ],
    complete: false
  },
  xStartingSounds: {
    character: {
      id: 31,
      character: 'J',
      characterCase: 'uppercase',
      characterType: 'print',
      locale: ['en-US']
    },
    level: 1,
    activities: [
      {
        id: 232,
        type: 'StartingSounds',
        level: 1,
        audio: [
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/these-words-start-with-the-letter-j.mp3',
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/j/jelly.mp3',
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/j/jeans.mp3',
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/j/jar.mp3'
        ],
        images: [
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/images/j/jelly.png',
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/images/j/jeans.png',
          'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/images/j/jar.png',
          'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/images/uppercase/flip-card-uppercase-j.png'
        ],
        data: {
          instructions: {
            beforeActivity:
              'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/these-words-start-with-the-letter-j.mp3'
          },
          rounds: [
            {
              image:
                'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/images/j/jelly.png',
              imageAudio:
                'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/j/jelly.mp3'
            },
            {
              image:
                'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/images/j/jeans.png',
              imageAudio:
                'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/j/jeans.mp3'
            },
            {
              image:
                'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/images/j/jar.png',
              imageAudio:
                'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/audio/j/jar.mp3'
            }
          ],
          animation:
            'https://gss-content-qa.s3.amazonaws.com/activities/starting-sound/videos/starting-sound-j.mp4',
          letterCard:
            'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/images/uppercase/flip-card-uppercase-j.png'
        }
      }
    ],
    complete: false
  },
  xDidYouKnow: {
    character: {
      id: 27,
      character: 'F',
      characterCase: 'uppercase',
      characterType: 'print',
      locale: ['en-US']
    },
    level: 1,
    activities: [
      {
        id: 669,
        type: 'DidYouKnow',
        level: 1,
        audio: [
          {
            id: 1602,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/audio/tap-the-card-to-hear-a-fact.mp3'
          },
          {
            id: 376,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/this-is-lowercase-c.mp3',
            vtt:
              'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/this-is-lowercase-c.vtt'
          },
          {
            id: 897,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/touch-the-letter-to-flip-the-card.mp3',
            vtt:
              'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/touch-the-letter-to-flip-the-card.vtt'
          }
        ],
        images: [
          {
            id: 449,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/images/lowercase/flip-card-lowercase-c.png',
            description: 'Flip card lowercase c'
          },
          {
            id: 1601,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/images/did-you-know-coconut.png',
            description: 'Card with coconut image on it'
          }
        ],
        data: {
          instructions: {
            afterFlip: {
              id: 1602,
              src:
                'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/audio/tap-the-card-to-hear-a-fact.mp3'
            },
            beforeFlip: [
              {
                id: 376,
                src:
                  'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/this-is-lowercase-c.mp3',
                vtt:
                  'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/this-is-lowercase-c.vtt'
              },
              {
                id: 897,
                src:
                  'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/touch-the-letter-to-flip-the-card.mp3',
                vtt:
                  'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/audio/touch-the-letter-to-flip-the-card.vtt'
              }
            ]
          },
          cardFront: {
            id: 449,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/flip-card/images/lowercase/flip-card-lowercase-c.png',
            description: 'Flip card lowercase c'
          },
          cardBack: {
            id: 1601,
            src:
              'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/images/did-you-know-coconut.png',
            description: 'Card with coconut image on it'
          },
          rounds: [
            {
              id: 1603,
              src:
                'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/videos/did-you-know-coconut-lifespan.mp4',
              description: 'Coconut lifespan video'
            },
            {
              id: 1604,
              src:
                'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/videos/did-you-know-coconut-climate.mp4',
              description: 'Coconut climate video'
            },
            {
              id: 1605,
              src:
                'https://gss-content-qa.s3.amazonaws.com/activities/did-you-know/videos/did-you-know-coconut-nutrition.mp4',
              description: 'Coconut nutrition video'
            }
          ]
        }
      }
    ],
    complete: false
  }
};
